import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Film, Upload, Eye, EyeOff, Loader, CheckCircle, AlertCircle, Link } from 'lucide-react';
import { createMuxUpload, uploadVideoToMux, pollVideoStatus } from '../../../services/muxVideoService';
import { createWarmUp, updateWarmUp, fetchTalentForDropdown } from '../../../services/warmUpService';

const WarmUpFormModal = ({ warmUp = null, onSave, onClose }) => {
  const isEdit = !!warmUp;

  const [title, setTitle] = useState(warmUp?.title || '');
  const [description, setDescription] = useState(warmUp?.description || '');
  const [instructions, setInstructions] = useState(warmUp?.instructions || '');
  const [talentId, setTalentId] = useState(warmUp?.talent_id || '');
  const [published, setPublished] = useState(warmUp?.published || false);
  const [difficultyLevel, setDifficultyLevel] = useState(warmUp?.difficulty_level || '');
  const [talentList, setTalentList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Video upload state
  const [videoStage, setVideoStage] = useState(warmUp?.mux_playback_id ? 'has_video' : 'idle'); // idle | uploading | processing | has_video | error
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pendingVideoData, setPendingVideoData] = useState(null); // { muxPlaybackId, muxUploadId, muxAssetId }

  // Video mode: 'upload' | 'mux_id'
  const [videoMode, setVideoMode] = useState('upload');
  const [muxIdInput, setMuxIdInput] = useState('');
  // Edit mode: allow changing the existing Mux ID
  const [editingMuxId, setEditingMuxId] = useState(false);
  const [editMuxIdValue, setEditMuxIdValue] = useState(warmUp?.mux_playback_id || '');

  const fileInputRef = useRef(null);

  // Drag state for modal
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });

  useEffect(() => {
    fetchTalentForDropdown()?.then(setTalentList)?.catch(console.error);
  }, []);

  const handleDragStart = useCallback((clientX, clientY) => {
    setIsDragging(true);
    dragStart.current = { mouseX: clientX, mouseY: clientY, posX: position?.x, posY: position?.y };
  }, [position]);

  const handleDragMove = useCallback((clientX, clientY) => {
    if (!isDragging) return;
    setPosition({
      x: dragStart?.current?.posX + (clientX - dragStart?.current?.mouseX),
      y: dragStart?.current?.posY + (clientY - dragStart?.current?.mouseY),
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => handleDragMove(e?.clientX, e?.clientY);
    const onEnd = () => handleDragEnd();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onEnd); };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file?.type?.startsWith('video/')) { setError('Please select a valid video file.'); return; }
    setSelectedFile(file);
    setError(null);
  };

  const handleUploadVideo = async () => {
    if (!selectedFile) return;
    setError(null);
    setVideoStage('uploading');
    setUploadProgress(0);
    try {
      const { uploadUrl, videoId } = await createMuxUpload({ title: title || selectedFile?.name });
      await uploadVideoToMux(uploadUrl, selectedFile, (pct) => setUploadProgress(pct));
      setVideoStage('processing');
      setUploadProgress(100);
      const readyVideo = await pollVideoStatus(videoId, { maxAttempts: 72, intervalMs: 5000 });
      if (readyVideo?.status === 'errored') throw new Error('Video processing failed on Mux.');
      setPendingVideoData({ muxPlaybackId: readyVideo?.mux_playback_id, muxUploadId: videoId, muxAssetId: readyVideo?.mux_asset_id });
      setVideoStage('has_video');
    } catch (err) {
      setError(err?.message || 'Upload failed. Please try again.');
      setVideoStage('error');
    }
  };

  const handleSaveMuxId = () => {
    const trimmed = muxIdInput?.trim();
    if (!trimmed) { setError('Please enter a Mux playback ID.'); return; }
    setError(null);
    setPendingVideoData({ muxPlaybackId: trimmed, muxUploadId: null, muxAssetId: null });
    setVideoStage('has_video');
  };

  const handleSaveEditMuxId = () => {
    const trimmed = editMuxIdValue?.trim();
    if (!trimmed) { setError('Please enter a valid Mux playback ID.'); return; }
    setError(null);
    setPendingVideoData({ muxPlaybackId: trimmed, muxUploadId: pendingVideoData?.muxUploadId || null, muxAssetId: pendingVideoData?.muxAssetId || null });
    setEditingMuxId(false);
  };

  const handleReplaceVideo = () => {
    setVideoStage('idle');
    setPendingVideoData(null);
    setSelectedFile(null);
    setMuxIdInput('');
    setEditingMuxId(false);
    setError(null);
  };

  const getTalentDisplayName = (t) => t?.display_name || `${t?.first_name || ''} ${t?.last_name || ''}`?.trim();

  const handleSave = async () => {
    if (!title?.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const videoPayload = pendingVideoData
        ? { muxPlaybackId: pendingVideoData?.muxPlaybackId, muxUploadId: pendingVideoData?.muxUploadId, muxAssetId: pendingVideoData?.muxAssetId, muxStatus: 'ready' }
        : {};

      let result;
      if (isEdit) {
        result = await updateWarmUp(warmUp?.id, {
          title: title?.trim(),
          description: description?.trim(),
          instructions: instructions?.trim(),
          talentId: talentId || null,
          published,
          difficultyLevel: difficultyLevel || null,
          ...videoPayload,
        });
      } else {
        result = await createWarmUp({
          title: title?.trim(),
          description: description?.trim(),
          instructions: instructions?.trim(),
          talentId: talentId || null,
          published,
          difficultyLevel: difficultyLevel || null,
          ...videoPayload,
        });
      }
      onSave?.(result);
    } catch (err) {
      setError(err?.message || 'Failed to save warm up.');
    } finally {
      setSaving(false);
    }
  };

  const currentMuxPlaybackId = pendingVideoData?.muxPlaybackId || warmUp?.mux_playback_id;
  const showHasVideo = videoStage === 'has_video' || (videoStage === 'idle' && currentMuxPlaybackId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        style={{ transform: `translate(${position?.x}px, ${position?.y}px)`, transition: isDragging ? 'none' : 'transform 0.05s ease', userSelect: isDragging ? 'none' : 'auto' }}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-5 border-b border-gray-200 rounded-t-xl ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          onMouseDown={(e) => { e?.preventDefault(); handleDragStart(e?.clientX, e?.clientY); }}
        >
          <div className="flex items-center gap-2 pointer-events-none">
            <Film className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Warm Up' : 'New Warm Up'}</h2>
            <span className="text-xs text-gray-400 ml-1">— drag to move</span>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors pointer-events-auto" onMouseDown={(e) => e?.stopPropagation()}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e?.target?.value)}
              placeholder="e.g. Morning Vocal Warm Up"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Brief Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e?.target?.value)}
              placeholder="Short summary shown on the card"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e?.target?.value)}
              placeholder="Detailed instructions for the student on how to perform this warm up..."
              rows={5}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>

          {/* Talent Association */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Associated Talent</label>
            <select
              value={talentId}
              onChange={(e) => setTalentId(e?.target?.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              <option value="">— No talent associated —</option>
              {talentList?.map((t) => (
                <option key={t?.id} value={t?.id}>{getTalentDisplayName(t)}</option>
              ))}
            </select>
          </div>

          {/* Difficulty Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty Level</label>
            <select
              value={difficultyLevel}
              onChange={(e) => setDifficultyLevel(e?.target?.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              <option value="">— No level assigned —</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
            </select>
          </div>

          {/* Video Section */}
          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Film className="w-4 h-4 text-indigo-500" />
              Video
            </p>

            {/* Has video — show current video info */}
            {showHasVideo && !editingMuxId && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-green-700 font-medium">Video ready</p>
                  <p className="text-xs text-gray-500 font-mono truncate">{currentMuxPlaybackId}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => { setEditingMuxId(true); setEditMuxIdValue(currentMuxPlaybackId || ''); setError(null); }}
                    className="text-xs text-indigo-600 hover:text-indigo-800 transition-colors font-medium"
                  >
                    Edit ID
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={handleReplaceVideo}
                    className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                  >
                    Replace
                  </button>
                </div>
              </div>
            )}

            {/* Edit Mux ID inline */}
            {showHasVideo && editingMuxId && (
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-600">Mux Playback ID</label>
                <input
                  type="text"
                  value={editMuxIdValue}
                  onChange={(e) => setEditMuxIdValue(e?.target?.value)}
                  placeholder="e.g. abc123xyz..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSaveEditMuxId}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium py-2 rounded-lg transition-colors"
                  >
                    Save ID
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEditingMuxId(false); setError(null); }}
                    className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium py-2 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Idle — show upload/mux-id tabs */}
            {!showHasVideo && videoStage !== 'uploading' && videoStage !== 'processing' && (
              <>
                {/* Mode tabs */}
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => { setVideoMode('upload'); setError(null); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                      videoMode === 'upload' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Upload Video
                  </button>
                  <button
                    type="button"
                    onClick={() => { setVideoMode('mux_id'); setError(null); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                      videoMode === 'mux_id' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Link className="w-4 h-4" />
                    Enter Mux ID
                  </button>
                </div>

                {/* Upload tab */}
                {videoMode === 'upload' && (
                  <>
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                      onClick={() => fileInputRef?.current?.click()}
                      onDragOver={(e) => e?.preventDefault()}
                      onDrop={(e) => { e?.preventDefault(); const f = e?.dataTransfer?.files?.[0]; if (f) setSelectedFile(f); }}
                    >
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-700">{selectedFile ? selectedFile?.name : 'Click or drag a video file here'}</p>
                      <p className="text-xs text-gray-500 mt-1">{selectedFile ? `${(selectedFile?.size / 1024 / 1024)?.toFixed(1)} MB` : 'MP4, MOV, WebM supported'}</p>
                      <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleFileSelect} />
                    </div>
                    {selectedFile && (
                      <button
                        type="button"
                        onClick={handleUploadVideo}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 rounded-lg transition-colors text-sm"
                      >
                        Upload to Mux
                      </button>
                    )}
                  </>
                )}

                {/* Enter Mux ID tab */}
                {videoMode === 'mux_id' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Mux Playback ID</label>
                      <input
                        type="text"
                        value={muxIdInput}
                        onChange={(e) => setMuxIdInput(e?.target?.value)}
                        placeholder="e.g. abc123xyz..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">Enter an existing Mux playback ID to link this video.</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleSaveMuxId}
                      disabled={!muxIdInput?.trim()}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2 rounded-lg transition-colors text-sm"
                    >
                      Save Mux ID
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Uploading */}
            {videoStage === 'uploading' && (
              <div className="space-y-2 py-2">
                <div className="flex items-center gap-2">
                  <Loader className="w-4 h-4 text-indigo-600 animate-spin" />
                  <p className="text-sm text-gray-700">Uploading... {uploadProgress}%</p>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-indigo-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}

            {/* Processing */}
            {videoStage === 'processing' && (
              <div className="flex items-center gap-3 py-3">
                <Loader className="w-5 h-5 text-indigo-600 animate-spin" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Processing on Mux...</p>
                  <p className="text-xs text-gray-500">This may take a few minutes.</p>
                </div>
              </div>
            )}

            {/* Error retry */}
            {videoStage === 'error' && (
              <button type="button" onClick={() => setVideoStage('idle')} className="text-sm text-indigo-600 hover:underline">
                Try again
              </button>
            )}
          </div>

          {/* Published toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-700">Published</p>
              <p className="text-xs text-gray-500">Make this warm up visible to students</p>
            </div>
            <button
              type="button"
              onClick={() => setPublished(!published)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${published ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
            >
              {published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {published ? 'Published' : 'Draft'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-gray-200">
          <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2.5 rounded-lg transition-colors text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !title?.trim()}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
          >
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Warm Up'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarmUpFormModal;
